import {
  type DataModelValue,
  type FieldAction,
  type FieldActionToBeProcessed,
  type FormEventPayload,
  type FormValue,
  type NameValue,
  type Question,
  type FieldRule,
  type FieldRuleFilter,
  ElementType,
  FieldActionType,
  AnswerType,
  FormFieldEvent,
} from "@archetypes/form";
import type { FormResponse } from "@archetypes/responses";
import { LOGICAL_OPERATOR, RELATIONAL_OPERATOR } from "@archetypes/operator";
import { isNullOrUndefined } from "./data-type.util";
import {
  getCurrentDate,
  getCurrentTime,
  getDefaultUsers,
  getDefaultValuesForForm,
} from "./default-values.util";
import type { AWSUser, User } from "@archetypes/auth";
import type { CurrencyValue } from "@components/currency";
import { compareAsc, isEqual, parseISO } from "date-fns";
import type { Entity } from "@archetypes/gql";
import type { PhoneNumberValue } from "@components/phone-number";
import {
  getDataModelPropertyValue,
  isDataModelOrRepeaterValue,
  isSubsetArray,
  setProperty,
} from "./generic.util";
import { formatResponseFilter } from "./format-response-filter.util";
import { evaluateMathExpression } from "./math-expression-evaluator.util";
import { getFileTypeFromUrl } from "./file-upload.util";
import type { FileTypeEnum } from "@archetypes/form-element";
import { CURRENT_DATE, CURRENT_TIME } from "@constants/global";
import { extractQuestionMetaFromMentionTag } from "./formula.util";

type IOperatorElementMachine = {
  [id in RELATIONAL_OPERATOR]?: {
    [key: string]: (value: { ruleValue?: FormValue; value?: FormValue }) => boolean;
  };
};

// TODO add support for all questions and all relational operators
export const Optiumus: IOperatorElementMachine = {
  [RELATIONAL_OPERATOR.EQUAL]: {
    [ElementType.SingleLineInputBox]: ({ ruleValue, value }) => {
      if (ruleValue === (value as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.UniqueId]: ({ ruleValue, value }) => {
      if (ruleValue === (value as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.DatePicker]: ({ ruleValue, value }) => {
      const ruleValueDate = parseISO(ruleValue as string);
      const valueDate = parseISO(value as string);
      if (compareAsc(ruleValueDate, valueDate) === 0) {
        return true;
      }
      return false;
    },
    [ElementType.Number]: ({ ruleValue, value }) => {
      if (ruleValue === (value as number)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if (ruleValue === (value as number)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MultiLineInputBox]: ({ ruleValue, value }) => {
      if (ruleValue === (value as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MultiChoice]: ({ ruleValue, value }) => {
      if (
        JSON.stringify((value as string[]).sort()) ===
        JSON.stringify((ruleValue as string[]).sort())
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.SingleChoice]: ({ ruleValue, value }) => {
      if ((value as string) === ruleValue) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.YesNo]: ({ ruleValue, value }) => {
      if ((value as boolean) === ruleValue) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        (value as CurrencyValue)?.amount === (ruleValue as CurrencyValue)?.amount &&
        (value as CurrencyValue)?.currencyCode === (ruleValue as CurrencyValue)?.currencyCode
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Email]: ({ value, ruleValue }) => {
      if (ruleValue === (value as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Entities]: ({ value, ruleValue }) => {
      if (
        (ruleValue as Entity[])?.every((entity) =>
          (value as Entity[])?.some((v) => v._id === entity._id),
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Link]: ({ value, ruleValue }) => {
      if (ruleValue === (value as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MobileNumber]: ({ value, ruleValue }) => {
      if (
        (ruleValue as PhoneNumberValue)?.number === (value as PhoneNumberValue)?.number &&
        (ruleValue as PhoneNumberValue)?.countryAndDialCode ===
          (value as PhoneNumberValue)?.countryAndDialCode
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MultipleDropdownSelect]: ({ value, ruleValue }) => {
      if (
        JSON.stringify((value as string[]).sort()) ===
        JSON.stringify((ruleValue as string[]).sort())
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Name]: ({ value, ruleValue }) => {
      if (
        (value as NameValue)?.firstName === (ruleValue as NameValue)?.firstName &&
        (value as NameValue)?.lastName === (ruleValue as NameValue)?.lastName
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Ranking]: ({ value, ruleValue }) => {
      if ((value as string[]).join("") === (ruleValue as string[]).join("")) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Rating]: ({ value, ruleValue }) => {
      if (ruleValue === (value as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.SingleDropdownSelect]: ({ value, ruleValue }) => {
      if ((value as string) === ruleValue) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Slider]: ({ value, ruleValue }) => {
      if ((value as number[]).join("") === (ruleValue as number[]).join("")) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.TimePicker]: ({ value, ruleValue }) => {
      if ((value as string) === ruleValue) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Users]: ({ value, ruleValue }) => {
      if (
        (ruleValue as User[])?.every((user) => (value as User[])?.some((v) => v.sub === user.sub))
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Formula]: ({ value, ruleValue }) => {
      if ((value as string) === ruleValue) {
        return true;
      } else {
        return false;
      }
    },
  },
  [RELATIONAL_OPERATOR.NOT_EQUAL]: {
    [ElementType.SingleLineInputBox]: ({ ruleValue, value }) => {
      if (ruleValue === (value as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.UniqueId]: ({ ruleValue, value }) => {
      if (ruleValue === (value as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Number]: ({ ruleValue, value }) => {
      if (ruleValue !== (value as number)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if (ruleValue !== (value as number)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MultiLineInputBox]: ({ ruleValue, value }) => {
      if (ruleValue === (value as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.MultiChoice]: ({ ruleValue, value }) => {
      if (
        JSON.stringify((value as string[]).sort()) ===
        JSON.stringify((ruleValue as string[]).sort())
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.SingleChoice]: ({ ruleValue, value }) => {
      if ((value as string) === ruleValue) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.YesNo]: ({ ruleValue, value }) => {
      if ((value as boolean) === ruleValue) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        (value as CurrencyValue)?.amount === (ruleValue as CurrencyValue)?.amount &&
        (value as CurrencyValue)?.currencyCode === (ruleValue as CurrencyValue)?.currencyCode
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.DatePicker]: ({ value, ruleValue }) => {
      if (isEqual(parseISO(value as string), parseISO(ruleValue as string))) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Email]: ({ value, ruleValue }) => {
      if (ruleValue === (value as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Entities]: ({ value, ruleValue }) => {
      if (
        (ruleValue as Entity[])?.every((entity) =>
          (value as Entity[])?.some((v) => v._id === entity._id),
        )
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Link]: ({ value, ruleValue }) => {
      if (ruleValue === (value as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.MobileNumber]: ({ value, ruleValue }) => {
      if (
        (ruleValue as PhoneNumberValue)?.number === (value as PhoneNumberValue)?.number &&
        (ruleValue as PhoneNumberValue)?.countryAndDialCode ===
          (value as PhoneNumberValue)?.countryAndDialCode
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.MultipleDropdownSelect]: ({ value, ruleValue }) => {
      if (
        JSON.stringify((value as string[]).sort()) ===
        JSON.stringify((ruleValue as string[]).sort())
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Name]: ({ value, ruleValue }) => {
      if (
        (value as NameValue)?.firstName === (ruleValue as NameValue)?.firstName &&
        (value as NameValue)?.lastName === (ruleValue as NameValue)?.lastName
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Ranking]: ({ value, ruleValue }) => {
      if ((value as string[]).join("") === (ruleValue as string[]).join("")) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Rating]: ({ value, ruleValue }) => {
      if (ruleValue === (value as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.SingleDropdownSelect]: ({ value, ruleValue }) => {
      if ((value as string) === ruleValue) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Slider]: ({ value, ruleValue }) => {
      if ((value as number[]).join("") === (ruleValue as number[]).join("")) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.TimePicker]: ({ value, ruleValue }) => {
      if ((value as string) === ruleValue) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Users]: ({ value, ruleValue }) => {
      if (
        (ruleValue as User[])?.every((user) => (value as User[])?.some((v) => v.sub === user.sub))
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Formula]: ({ value, ruleValue }) => {
      if ((value as string) === ruleValue) {
        return false;
      } else {
        return true;
      }
    },
  },
  [RELATIONAL_OPERATOR.INCLUDES]: {
    [ElementType.SingleLineInputBox]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.UniqueId]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MultiLineInputBox]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MultipleDropdownSelect]: ({ ruleValue, value }) => {
      if (isSubsetArray(ruleValue as string[], value as string[], (a, b) => a.localeCompare(b))) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MultiChoice]: ({ ruleValue, value }) => {
      if (isSubsetArray(ruleValue as string[], value as string[], (a, b) => a.localeCompare(b))) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Name]: ({ ruleValue, value }) => {
      const fullName =
        (ruleValue as NameValue)?.firstName ?? "" + (ruleValue as NameValue)?.lastName;
      const nameValue =
        ((value as NameValue)?.firstName || "") + ((value as NameValue)?.lastName || "");
      if (nameValue.includes(fullName)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Email]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Link]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.MobileNumber]: ({ ruleValue, value }) => {
      if (
        (value as PhoneNumberValue)?.number
          ?.toString()
          .includes((ruleValue as PhoneNumberValue)?.number?.toString() ?? "---")
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        (value as CurrencyValue)?.amount
          ?.toString()
          .includes((ruleValue as CurrencyValue)?.amount?.toString() ?? "---")
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Formula]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Users]: ({ ruleValue, value }) => {
      if (
        isSubsetArray(ruleValue as User[], value as User[], (a, b) => a.sub.localeCompare(b.sub))
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Entities]: ({ ruleValue, value }) => {
      if (
        isSubsetArray(ruleValue as Entity[], value as Entity[], (a, b) =>
          a._id.localeCompare(b._id),
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
  },
  [RELATIONAL_OPERATOR.NOT_INCLUDES]: {
    [ElementType.SingleLineInputBox]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.UniqueId]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.MultiLineInputBox]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.MultipleDropdownSelect]: ({ ruleValue, value }) => {
      if (isSubsetArray(ruleValue as string[], value as string[], (a, b) => a.localeCompare(b))) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.MultiChoice]: ({ ruleValue, value }) => {
      if (isSubsetArray(ruleValue as string[], value as string[], (a, b) => a.localeCompare(b))) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Name]: ({ ruleValue, value }) => {
      const fullName =
        (ruleValue as NameValue)?.firstName ?? "" + (ruleValue as NameValue)?.lastName;
      const nameValue =
        ((value as NameValue)?.firstName || "") + ((value as NameValue)?.lastName || "");

      if (nameValue.includes(fullName)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Email]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Link]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.MobileNumber]: ({ ruleValue, value }) => {
      if (
        (value as PhoneNumberValue)?.number
          ?.toString()
          .includes((ruleValue as PhoneNumberValue)?.number?.toString() ?? "---")
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        (value as CurrencyValue)?.amount
          ?.toString()
          .includes((ruleValue as CurrencyValue)?.amount?.toString() ?? "---")
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Formula]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if ((value as string).includes(ruleValue as string)) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Users]: ({ ruleValue, value }) => {
      if (
        isSubsetArray(ruleValue as User[], value as User[], (a, b) => a.sub.localeCompare(b.sub))
      ) {
        return false;
      } else {
        return true;
      }
    },
    [ElementType.Entities]: ({ ruleValue, value }) => {
      if (
        isSubsetArray(ruleValue as Entity[], value as Entity[], (a, b) =>
          a._id.localeCompare(b._id),
        )
      ) {
        return false;
      } else {
        return true;
      }
    },
  },
  [RELATIONAL_OPERATOR.IS_ANSWERED]: {
    [ElementType.Currency]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.UniqueId]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Number]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.DataModel]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.DatePicker]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.DateRangePicker]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Email]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Entities]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.FileUpload]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.ImageUpload]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Link]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.LocationPicker]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.MobileNumber]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.MultiChoice]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.MultiLineInputBox]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.MultipleDropdownSelect]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Name]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Ranking]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Rating]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.RichText]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Signature]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.SingleChoice]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.SingleDropdownSelect]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.SingleLineInputBox]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Slider]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.TermsAndConditons]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.TimePicker]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Users]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.YesNo]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Formula]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Aggregator]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
    [ElementType.Repeater]: ({ value }) => {
      return !isNullOrUndefined(value);
    },
  },
  [RELATIONAL_OPERATOR.IS_NOT_ANSWERED]: {
    [ElementType.Currency]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.UniqueId]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Number]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Formula]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Aggregator]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.DataModel]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.DatePicker]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.DateRangePicker]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Email]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Entities]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.FileUpload]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.ImageUpload]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Link]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.LocationPicker]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.MobileNumber]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.MultiChoice]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.MultiLineInputBox]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.MultipleDropdownSelect]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Name]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Ranking]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Rating]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.RichText]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Signature]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.SingleChoice]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.SingleDropdownSelect]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.SingleLineInputBox]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Slider]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.TermsAndConditons]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.TimePicker]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Users]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.YesNo]: ({ value }) => {
      return isNullOrUndefined(value);
    },
    [ElementType.Repeater]: ({ value }) => {
      return isNullOrUndefined(value);
    },
  },
  [RELATIONAL_OPERATOR.GREATER_THAN]: {
    [ElementType.Number]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) > parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.DatePicker]: ({ ruleValue, value }) => {
      const ruleValueDate = ruleValue === CURRENT_DATE ? new Date() : parseISO(ruleValue as string);
      const valueDate = parseISO(value as string);
      if (compareAsc(valueDate, ruleValueDate) > 0) {
        return true;
      }
      return false;
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) > parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Formula]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) > parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        parseFloat((value as CurrencyValue)?.amount as string) >
        parseFloat((ruleValue as CurrencyValue)?.amount as string)
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Rating]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) > parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
  },
  [RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL]: {
    [ElementType.Number]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) >= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.DatePicker]: ({ ruleValue, value }) => {
      const ruleValueDate = ruleValue === CURRENT_DATE ? new Date() : parseISO(ruleValue as string);
      const valueDate = parseISO(value as string);
      if (compareAsc(valueDate, ruleValueDate) >= 0) {
        return true;
      }
      return false;
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) >= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Formula]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) >= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        parseFloat((value as CurrencyValue)?.amount as string) >=
        parseFloat((ruleValue as CurrencyValue)?.amount as string)
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Rating]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) >= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
  },
  [RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL]: {
    [ElementType.Number]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) <= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.DatePicker]: ({ ruleValue, value }) => {
      const ruleValueDate = ruleValue === CURRENT_DATE ? new Date() : parseISO(ruleValue as string);
      const valueDate = parseISO(value as string);
      if (compareAsc(valueDate, ruleValueDate) <= 0) {
        return true;
      }
      return false;
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) <= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Formula]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) <= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        parseFloat((value as CurrencyValue)?.amount as string) <=
        parseFloat((ruleValue as CurrencyValue)?.amount as string)
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Rating]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) <= parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
  },
  [RELATIONAL_OPERATOR.LESS_THAN]: {
    [ElementType.Number]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) < parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.DatePicker]: ({ ruleValue, value }) => {
      const ruleValueDate = ruleValue === CURRENT_DATE ? new Date() : parseISO(ruleValue as string);
      const valueDate = parseISO(value as string);
      if (compareAsc(valueDate, ruleValueDate) < 0) {
        return true;
      }
      return false;
    },
    [ElementType.Aggregator]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) < parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Formula]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) < parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Currency]: ({ ruleValue, value }) => {
      if (
        parseFloat((value as CurrencyValue)?.amount as string) <
        parseFloat((ruleValue as CurrencyValue)?.amount as string)
      ) {
        return true;
      } else {
        return false;
      }
    },
    [ElementType.Rating]: ({ ruleValue, value }) => {
      if (parseFloat(value as string) < parseFloat(ruleValue as string)) {
        return true;
      } else {
        return false;
      }
    },
  },
  [RELATIONAL_OPERATOR.FILE_NAME_CONTAINS]: {
    [ElementType.FileUpload]: ({ ruleValue, value }) => {
      if (Array.isArray(value)) {
        if ((value as string[])?.join(",").includes(ruleValue as string)) {
          return true;
        } else {
          return false;
        }
      } else {
        if ((value as string)?.includes(ruleValue as string)) {
          return true;
        } else {
          return false;
        }
      }
    },
    [ElementType.ImageUpload]: ({ ruleValue, value }) => {
      if (Array.isArray(value)) {
        if ((value as string[])?.join(",").includes(ruleValue as string)) {
          return true;
        } else {
          return false;
        }
      } else {
        if ((value as string)?.includes(ruleValue as string)) {
          return true;
        } else {
          return false;
        }
      }
    },
  },
  [RELATIONAL_OPERATOR.HAS_FILE_TYPE]: {
    [ElementType.FileUpload]: ({ ruleValue, value }) => {
      if (Array.isArray(value)) {
        if (
          (value as string[])
            ?.map((url) => getFileTypeFromUrl(url))
            .includes(ruleValue as FileTypeEnum)
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        if (getFileTypeFromUrl(value as string) === (ruleValue as FileTypeEnum)) {
          return true;
        } else {
          return false;
        }
      }
    },
    [ElementType.ImageUpload]: ({ ruleValue, value }) => {
      if (Array.isArray(value)) {
        if (
          (value as string[])
            ?.map((url) => getFileTypeFromUrl(url))
            .includes(ruleValue as FileTypeEnum)
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        if (getFileTypeFromUrl(value as string) === (ruleValue as FileTypeEnum)) {
          return true;
        } else {
          return false;
        }
      }
    },
  },
};

const evaluateRuleResultBasedOnLogicalOperator = (
  ruleResult: boolean,
  isAnd: boolean,
  result?: boolean,
) => {
  if (typeof result === "undefined") {
    return ruleResult;
  } else {
    return isAnd ? result && ruleResult : result || ruleResult;
  }
};

// Sample Workflow-> [AND, {questionId, questionType, operator, value}, [OR, {questionId, questionType, operator, value}, {questionId, questionType, operator, value}], true]
// export const evaluateRule = (ruleFilter: RuleFilter, formValues?: FormResponse) => {
//   const logicalOperator = ruleFilter[0] as LOGICAL_OPERATOR;
//   const isAnd = logicalOperator === LOGICAL_OPERATOR.AND;
//   let result: boolean | undefined;
//   for (let i = 1; i < ruleFilter.length; i++) {
//     const ruleData = ruleFilter[i];
//     if (typeof ruleData === "boolean") {
//       result = evaluateRuleResultBasedOnLogicalOperator(ruleData, isAnd, result); // If we have an operator
//     } else if (Array.isArray(ruleData)) {
//       result = evaluateRule(ruleData, formValues); // If we have a sub-tree
//     } else {
//       // If we have a leaf node i.e RuleNode
//       const ruleNode = ruleData as RuleNode;
//       const { operator, questionType, value: ruleValue, questionId } = ruleNode;
//       const currentValue = formValues?.[questionId];
//       const operatorResult = Optiumus[operator]?.[questionType]?.(ruleValue, currentValue) || false;

//       if (operatorResult && !isAnd) {
//         // If result is true and it is an OR operator then early exit as TRUE
//         result = true;
//         break;
//       } else if (!operatorResult && isAnd) {
//         // If result is false and it is an AND operator then early exit as FALSE
//         result = false;
//         break;
//       }
//       result = evaluateRuleResultBasedOnLogicalOperator(operatorResult, isAnd, result);
//     }
//   }
//   return !!result;
// };

export const evaluateFieldRuleFilter = ({
  ruleFilter,
  formValues,
}: {
  ruleFilter: FieldRuleFilter;
  formValues?: FormResponse;
}) => {
  const logicalOperator = ruleFilter.logicalOperator as LOGICAL_OPERATOR;
  const isAnd = logicalOperator === LOGICAL_OPERATOR.AND;
  let result: boolean | undefined;

  const fieldsets = ruleFilter.fieldsets;
  for (let i = 0; i < fieldsets.length; i++) {
    const { questionId, questionType, relationalOperator, answer } = fieldsets[i];
    if (questionId && questionType) {
      const questionKey = questionId.includes(".") ? questionId.split(".")[0] : questionId;
      const isDataModelOrRepeaterField =
        questionId.includes(".") && isDataModelOrRepeaterValue(formValues?.[questionKey]);

      const value = isDataModelOrRepeaterField
        ? getDataModelPropertyValue(
            formValues?.[questionKey] as DataModelValue[],
            questionId.slice(questionId.indexOf(".") + 1),
          )
        : formValues?.[questionKey];
      const operatorResult = (() => {
        if (!isDataModelOrRepeaterField) {
          return !!Optiumus[relationalOperator]?.[questionType]?.({
            ruleValue: answer,
            value: value as FormValue,
          });
        } else {
          return (value as FormValue[]).some((val) =>
            Optiumus[relationalOperator]?.[questionType]?.({
              ruleValue: answer,
              value: val,
            }),
          );
        }
      })();

      if (operatorResult && !isAnd) {
        // If result is true and it is an OR operator then early exit as TRUE
        result = true;
        break;
      } else if (!operatorResult && isAnd) {
        // If result is false and it is an AND operator then early exit as FALSE
        result = false;
        break;
      }
      result = evaluateRuleResultBasedOnLogicalOperator(operatorResult, isAnd, result);
    }
  }
  return !!result;
};

export const getEventRuleMap = (fieldRules: FieldRule[]) => {
  const eventRuleMap: Record<string, string[]> = {};
  fieldRules.forEach((fieldRule) => {
    if (!fieldRule.enabled) {
      return;
    }
    if (fieldRule.action.type === FieldActionType.VALIDATION) {
      return;
    } else {
      const valueType = fieldRule?.action?.valueType;
      const value = fieldRule?.action?.value;
      let isDynamic: boolean = false;
      if (valueType === AnswerType.DYNAMIC && !isNullOrUndefined(value)) {
        // If there is a dynamic value binding then everytime the value of binded field changes then the rule should be evaluated
        const key = (value as string) + "_onChange";
        eventRuleMap[key] = [...new Set([...(eventRuleMap[key] ?? []), fieldRule.id])];
        isDynamic = true;
      }

      if (!fieldRule?.filter?.fieldsets?.length && !isDynamic) {
        // If there is no filter in a rule it should be evaluated as soon as form is loaded
        eventRuleMap["onFormLoad"] = [
          ...new Set([...(eventRuleMap["onFormLoad"] ?? []), fieldRule.id]),
        ];
        return;
      }

      fieldRule?.filter?.fieldsets.forEach((field) => {
        let event = "";
        if (field.questionId) {
          event += field.questionId.split(".")[0]; // To extract just parent question id incase of DM
          if (
            [
              RELATIONAL_OPERATOR.EQUAL,
              RELATIONAL_OPERATOR.NOT_EQUAL,
              RELATIONAL_OPERATOR.INCLUDES,
              RELATIONAL_OPERATOR.GREATER_THAN,
              RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
              RELATIONAL_OPERATOR.LESS_THAN,
              RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
              RELATIONAL_OPERATOR.NOT_INCLUDES,
              RELATIONAL_OPERATOR.IS_ANSWERED,
              RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
              RELATIONAL_OPERATOR.LIES_BETWEEN,
              RELATIONAL_OPERATOR.GREATER_THAN,
              RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
              RELATIONAL_OPERATOR.LESS_THAN,
              RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
            ].includes(field.relationalOperator)
          ) {
            event += "_onChange";
          }
          eventRuleMap[event] = [...new Set([...(eventRuleMap[event] ?? []), fieldRule.id])];
        } else {
          event = "onFormLoad";
          eventRuleMap[event] = [...new Set([...(eventRuleMap[event] ?? []), fieldRule.id])];
        }
      });
    }
  });
  return eventRuleMap;
};

export const getAntiActions = (action: FieldAction) => {
  switch (action.type) {
    case FieldActionType.DISPLAY_FIELD:
      return [{ ...action, type: FieldActionType.HIDE_FIELD }];
    case FieldActionType.HIDE_FIELD:
      return [{ ...action, type: FieldActionType.DISPLAY_FIELD }];
    case FieldActionType.EVALUATE_DATA_VIEW_FILTER:
      return [
        { ...action, type: FieldActionType.UNSET_VALUE },
        {
          ...action,
          type: FieldActionType.UPDATE_PROPERTY,
          property: "content.dataModel.updatedFilter",
          value: undefined,
        },
      ];
    case FieldActionType.EVALUATE_FORMULA_EXPRESSION:
      return [{ ...action, type: FieldActionType.UNSET_VALUE }];
    case FieldActionType.EVALUATE_AGGREGATOR:
      return [
        { ...action, type: FieldActionType.UNSET_VALUE },
        {
          ...action,
          type: FieldActionType.UPDATE_PROPERTY,
          property: "content.aggregator.updatedFilter",
          value: undefined,
        },
      ];
    default:
      return [];
  }
};

export const evaluateRulesAndGetActionsToBeProcessed = ({
  events,
  ruleTriggers,
  fieldRulesMap,
  formValues,
  questionsMap,
}: {
  events: FormEventPayload[];
  ruleTriggers: Record<string, string[]>;
  fieldRulesMap: Record<string, FieldRule>;
  questionsMap: Record<string, Question>;
  formValues?: FormResponse;
}) => {
  const actionsToBeProcessed: FieldActionToBeProcessed[] = [];
  events.forEach((event) => {
    const eventType = event.questionId ? event.questionId + "_" + event.type : event.type;
    const ruleIds = ruleTriggers[eventType] ?? [];

    ruleIds.forEach((ruleId) => {
      const rule = fieldRulesMap[ruleId];
      if (!rule || !rule.enabled) {
        return;
      }
      // If there is no filter just run the action else evaluate the filter
      const result = rule.filter
        ? evaluateFieldRuleFilter({
            formValues,
            ruleFilter: rule.filter,
          })
        : true;
      if (result) {
        actionsToBeProcessed.push({
          ...rule.action,
          question: questionsMap[rule.questionId],
        });
      } else {
        // If filter doesn't evaluate well run the opposite action i.e if visible > hide field or vice-versa
        const oppositeActions = getAntiActions(rule.action);
        oppositeActions.forEach((action) => {
          actionsToBeProcessed.push({
            ...action,
            question: questionsMap[rule.questionId],
          });
        });
      }
    });
  });
  return actionsToBeProcessed;
};

export const getEventsFromDefaultValues = ({
  questions,
  users,
  defaultValues,
  loggedInUser,
}: {
  questions: Question[];
  users: User[];
  defaultValues?: FormResponse;
  loggedInUser: AWSUser;
}) => {
  const formDefaultValues = getDefaultValuesForForm({
    questions,
    users,
    loggedInUser,
    defaultValues,
  });
  const events: FormEventPayload[] = [];
  questions.forEach((question) => {
    events.push({
      type: FormFieldEvent.ON_VALUE_CHANGE,
      questionId: question.id,
      value: formDefaultValues[question.id],
    }); // triggering onChange for questions no matter the default value has been set or not set
  });

  return events;
};

export const getFieldEventsAndUpdatedQuestionsMap = ({
  actionsToBeProcessed,
  formValues,
  currentUser,
}: {
  actionsToBeProcessed: FieldActionToBeProcessed[];
  formValues: FormResponse;
  currentUser?: User;
}) => {
  const questionsUpdateMap: Record<string, Question> = {};
  const newEvents: FormEventPayload[] = [];
  let doesFormValueChange: boolean = false;
  if (actionsToBeProcessed.length === 0) {
    return {
      questionsUpdateMap,
      newEvents,
      doesFormValueChange,
      updatedFormValues: formValues,
    };
  }
  const updatedFormValues = structuredClone(formValues);
  actionsToBeProcessed.forEach((action) => {
    const question = action.question;
    if (!question) {
      return;
    }
    switch (action.type) {
      case FieldActionType.UNSET_VALUE:
        {
          updatedFormValues[question.id] = null;
          newEvents.push({
            type: FormFieldEvent.ON_VALUE_CHANGE,
            questionId: question.id,
            value: null,
          });
          doesFormValueChange = true;
        }
        break;
      case FieldActionType.SET_VALUE:
        {
          let updatedValue =
            action.valueType === AnswerType.DYNAMIC
              ? updatedFormValues[action.value as string]
              : action.value;

          if (question.type === ElementType.DatePicker && updatedValue === CURRENT_DATE) {
            updatedValue = getCurrentDate();
          } else if (question.type === ElementType.TimePicker && updatedValue === CURRENT_TIME) {
            updatedValue = getCurrentTime(question.content.timePicker?.timeFormat);
          } else if (question.type === ElementType.Users && currentUser) {
            return getDefaultUsers({
              isMultiChoice: question?.content?.user?.isMultiChoice,
              currentUser,
              defaultValue: updatedValue as User[],
            });
          }
          updatedFormValues[question.id] = updatedValue;
          newEvents.push({
            type: FormFieldEvent.ON_VALUE_CHANGE,
            questionId: question.id,
            value: updatedValue,
          });
          doesFormValueChange = true;
        }
        break;
      case FieldActionType.HIDE_FIELD:
        {
          questionsUpdateMap[question.id] = {
            ...question,
            content: {
              ...question.content,
              visible: false,
            },
          };
        }
        break;
      case FieldActionType.DISPLAY_FIELD:
        {
          const question = action.question;
          questionsUpdateMap[question.id] = {
            ...question,
            content: {
              ...question.content,
              visible: true,
            },
          };
        }
        break;
      case FieldActionType.EVALUATE_DATA_VIEW_FILTER:
        {
          const question = action.question;
          const updatedFilter = formatResponseFilter({
            filter: question?.content?.dataModel?.filter,
            formValues: updatedFormValues,
          });

          questionsUpdateMap[question.id] = {
            ...question,
            content: {
              ...question.content,
              dataModel: question.content.dataModel
                ? { ...question.content.dataModel, updatedFilter }
                : undefined,
            },
          };
        }
        break;
      case FieldActionType.EVALUATE_AGGREGATOR:
        {
          const question = action.question;
          const updatedFilter = formatResponseFilter({
            filter: question?.content?.aggregator?.filter,
            formValues: updatedFormValues,
          });

          questionsUpdateMap[question.id] = {
            ...question,
            content: {
              ...question.content,
              aggregator: question.content.aggregator
                ? {
                    ...question.content.aggregator,
                    updatedFilter,
                    questionValue: question.content.aggregator?.offlineAggregation
                      ? (updatedFormValues?.[
                          question.content.aggregator.questionId as string
                        ] as DataModelValue[])
                      : undefined,
                  }
                : undefined,
            },
          };
        }
        break;
      case FieldActionType.EVALUATE_FORMULA_EXPRESSION:
        {
          const expression = question.content?.formula?.expression;
          const questionsRecord = extractQuestionMetaFromMentionTag(expression ?? "");
          const precision = question.content?.formula?.precision;
          if (!expression || !precision) {
            return;
          }
          const value = evaluateMathExpression({
            expression,
            precision,
            formValues: updatedFormValues,
            questionsRecord,
          });
          if (!isNullOrUndefined(value)) {
            updatedFormValues[question.id] = value?.toString();
            newEvents.push({
              type: FormFieldEvent.ON_VALUE_CHANGE,
              questionId: question.id,
              value: value?.toString(),
            });
            doesFormValueChange = true;
          }
        }
        break;
      case FieldActionType.UPDATE_PROPERTY: {
        if (!action.property) {
          return;
        }
        const updatedQuestion = setProperty(action.question, action.property, action.value);
        questionsUpdateMap[question.id] = updatedQuestion;
        break;
      }
    }
  });

  return { questionsUpdateMap, newEvents, formValues: updatedFormValues };
};
